import { pdf } from '@react-pdf/renderer';
import { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAsync } from 'react-use';
import useTranslate from 'hooks/useTranslate';
import { convertPDFPageToImageBlob, extractRelevantResumeFields, imageBucketUrl } from 'utils';
import { ResumePreviewSkeleton } from 'components/Common/Skeleton/Resume';
import { S3_URL } from 'constants';
import useResumeStore from 'store/resume';
import {
	useBucketUrlMutation,
	useDownloadResumeMobileMutation,
	useDownloadResumeMutation,
	useSaveResumeMutation,
	useUploadFileMutation,
} from 'services';
import useCommonStore, { setIsLoading } from 'store/common';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import './styles.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TemplateLayout = ({ children, isWebView, token = '' }: any) => {
	const { resumeData } = useResumeStore((state: any) => state);
	const { translate } = useTranslate();
	const [loading, setLoading] = useState<boolean>(true);
	const { isLoading }: any = useCommonStore((state) => state);
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	// hooks
	const value = useMemo(() => {
		if (children?.props?.resumeData && Object.keys(children?.props?.resumeData).length) {
			return children;
		}
	}, [resumeData]);

	const scale = 1.5;
	const [numPages, setNumPages]: any = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [previousRenderValue, setPreviousRenderValue] = useState(null);

	const { mutateAsync: saveResumeMutation } = useSaveResumeMutation();
	const { mutateAsync: downloadResumeMutation } = useDownloadResumeMutation();
	const { mutateAsync: downloadResumeMutationMobile } = useDownloadResumeMobileMutation();

	const render: any = useAsync(async () => {
		if (!value) return null;

		if (value?.props?.resumeData && Object.keys(value?.props?.resumeData).length) {
			const blob = await pdf(value).toBlob();
			const url = URL.createObjectURL(blob);

			setLoading(false);
			return url;
		}
	}, [value, resumeData]);

	const onPreviousPage = () => {
		setCurrentPage((prev) => prev - 1);
	};

	const onNextPage = () => {
		setCurrentPage((prev) => prev + 1);
	};

	const onDocumentLoad = (d: any) => {
		setNumPages(d.numPages);
		setCurrentPage((prev) => Math.min(prev, d.numPages));
	};

	const downloadPDF = async () => {
		// Download the pdf
		setIsLoading(true);
		/* const saveResponse: boolean = isWebView ? true : await saveResume();
		if (saveResponse) { */
		// Download the pdf
		const link = document.createElement('a');
		link.style.display = 'none';
		document.body.appendChild(link);
		link.href = render?.value;
		link.setAttribute('download', 'resume.pdf');
		link.click();
		document.body.removeChild(link);
		// Upload to s3
		await uploadToS3();
		/* } */
		setIsLoading(false);
	};

	const saveResume = async () => {
		try {
			const filteredData: any = await extractRelevantResumeFields(resumeData);
			if (filteredData && Object.keys(filteredData).length) {
				const response = await saveResumeMutation({
					...filteredData,
					templateId: Number(resumeData.templateId),
				});

				return response?.success || false;
			}
			return false;
		} catch (error) {
			console.error('Error saving resume:', error);
			return false;
		}
	};

	const uploadToS3 = async () => {
		try {
			const pdfData = await pdfjs.getDocument(render.value).promise;
			const canvas = document.createElement('canvas');
			const imgData = canvas.toDataURL('image/png');
			if (!imgData || typeof imgData !== 'string' || !imgData.startsWith('data:image')) {
				throw new Error('Failed to convert canvas to base64');
			}

			const imgBlob: any = await convertPDFPageToImageBlob(canvas, pdfData);

			const imgPath = await imageBucketUrl(
				imgBlob,
				translate,
				'image/png',
				imgBlob.size,
				bucketUrlMutation,
				uploadFileMutation,
				'RESUME_BUILDER_IMAGE',
			);

			const blobPdf: any = await pdf(value).toBlob();
			blobPdf.name = `resume_${Date.now()}.pdf`;
			const pdfPath = await imageBucketUrl(
				blobPdf,
				translate,
				'application/pdf',
				blobPdf.size,
				bucketUrlMutation,
				uploadFileMutation,
				'RESUME_BUILDER_PDF',
			);

			downloadResumeMutation({
				templateId: resumeData.templateId,
				previewImage: imgPath,
				resumePath: pdfPath,
			});
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const uploadToS3Mobile = async () => {
		try {
			setIsLoading(true);
			const pdfData = await pdfjs.getDocument(render.value).promise;
			const canvas = document.createElement('canvas');
			const imgData = canvas.toDataURL('image/png');

			if (!imgData || typeof imgData !== 'string' || !imgData.startsWith('data:image')) {
				throw new Error('Failed to convert canvas to base64');
			}

			const imgBlob = await convertPDFPageToImageBlob(canvas, pdfData);
			const imgPath = await imageBucketUrl(
				imgBlob,
				translate,
				'image/png',
				imgBlob.size,
				bucketUrlMutation,
				uploadFileMutation,
				'RESUME_BUILDER_IMAGE',
			);

			const blobPdf: any = await pdf(value).toBlob();
			blobPdf.name = `resume_${Date.now()}.pdf`;

			const pdfPath = await imageBucketUrl(
				blobPdf,
				translate,
				'application/pdf',
				blobPdf.size,
				bucketUrlMutation,
				uploadFileMutation,
				'RESUME_BUILDER_PDF',
			);

			downloadResumeMutationMobile({
				templateId: resumeData.templateId,
				previewImage: imgPath,
				resumePath: pdfPath,
				accessToken: token,
			}).then((res) => {
				if (res?.success) {
					const fullPdfUrl = S3_URL + pdfPath;

					// Trigger download of the PDF file
					const link = document.createElement('a');
					link.href = fullPdfUrl;
					link.download = `resume_${Date.now()}.pdf`; // Specify the name of the downloaded file
					document.body.appendChild(link);
					link.click(); // Simulate a click to trigger the download
					document.body.removeChild(link); // Clean up the DOM
					setIsLoading(false);
					return fullPdfUrl; // Return the PDF URL
				} else {
					setIsLoading(false);
				}
			});
		} catch (error) {
			console.error('Error:', error);
			setIsLoading(false);
		}
	};

	const isFirstRendering = !previousRenderValue;
	const isLatestValueRendered = previousRenderValue === render.value;
	const isBusy = render.loading || !isLatestValueRendered;
	const shouldShowPreviousDocument = !isFirstRendering && isBusy;

	return (
		<>
			{loading ? (
				<ResumePreviewSkeleton />
			) : (
				<>
					<div className="resume-wrapper">
						<div className="resume-wrapper-document">
							{shouldShowPreviousDocument && previousRenderValue && (
								<Document
									key={previousRenderValue}
									className="previous-document"
									file={previousRenderValue}
									onLoadError={(error) => console.log(error)}
									loading={null}
									onContextMenu={(e) => e.preventDefault()}
								>
									<Page
										key={currentPage}
										pageNumber={currentPage}
										renderAnnotationLayer={false}
										renderTextLayer={false}
										devicePixelRatio={window.devicePixelRatio}
										scale={scale}
										loading={null}
									/>
								</Document>
							)}
							<Document
								key={render.value}
								className={shouldShowPreviousDocument ? 'rendering-document' : null}
								file={render.value}
								onLoadError={(error) => console.log(error)}
								onLoadSuccess={onDocumentLoad}
								loading={null}
								onContextMenu={(e) => e.preventDefault()}
							>
								<Page
									key={currentPage}
									pageNumber={currentPage}
									onRenderSuccess={() => {
										setPreviousRenderValue(render.value);
									}}
									renderAnnotationLayer={false}
									renderTextLayer={false}
									devicePixelRatio={window.devicePixelRatio}
									scale={scale}
									loading={null}
								/>
							</Document>
						</div>
					</div>

					<div className="text-center">
						<p className="page-count text-white mb-2">
							{translate('Template.page')} {currentPage} {translate('Template.of')} {numPages}
						</p>

						<a
							className="download-btn rounded-pill text-decoration-none"
							onClick={isWebView ? uploadToS3Mobile : downloadPDF}
						>
							{translate('Template.download')}
						</a>
					</div>
				</>
			)}

			{numPages > 1 && (
				<>
					{currentPage > 1 && (
						<button
							type="button"
							className="prev-btn position-absolute border-0"
							onClick={onPreviousPage}
						>
							<div className="prev-btn-icon"></div>
						</button>
					)}
					{currentPage < numPages && (
						<button
							type="button"
							className="next-btn position-absolute border-0"
							onClick={onNextPage}
						>
							<div className="next-btn-icon"></div>
						</button>
					)}
				</>
			)}
		</>
	);
};

export default TemplateLayout;
